import React, { useState, useRef } from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Product from './Product';
import Button from '@mui/material/Button';
import ProductFilters from './ProductFilters';
import MaterialFilterBar from './MaterialFilterBar';
import ProductCardSkeleton from './ProductCardSkeleton';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import CircularProgress from '@mui/material/CircularProgress';
import axios from 'axios';
import { useParams, useLocation, Link as RouterLink } from 'react-router-dom';
import { useEffect, useContext } from 'react';
import { GlobalContext } from '../context/GlobalState';
import { materialDisplayName } from '../utils/materialLabels';
import { strings } from 'localisation';
import createGAevent from 'helpers';
import fs from 'fs';
import { Helmet } from 'react-helmet';
import { languages, routerUrls } from 'constants';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import ProductSeoBody, { ProductSeoHeader, getProductSeoMeta } from './ProductSeoContent';

// Names the product the dimension-filter heading is about, so "180 × 200 × 16-20 см" reads as
// "Чаршафи за матрак 180 × 200 × 16-20 см". Fitted and round sheets are filtered by the mattress's
// dimensions, everything else by the item's own - the wording has to say which. Keyed by
// subCategoryId (ids come from /api/menu), same as SIZE_HEADING_BY_SUBCATEGORY in
// ProductViewGridItemAction.jsx.
const FILTER_HEADING_PRODUCT_BY_SUBCATEGORY = {
  1: { bg: 'Чаршафи', en: 'Sheets' }, // Чаршафи
  2: { bg: 'Калъфки за възглавници', en: 'Pillowcases' }, // Калъфки за възглавници
  3: { bg: 'Чаршафи за матрак', en: 'Fitted sheets for a mattress' }, // Чаршафи с ластик
  4: { bg: 'Пликове за завивка', en: 'Duvet covers' }, // Пликове за завивка
  6: { bg: 'Кръгли чаршафи за матрак', en: 'Round fitted sheets for a mattress' }, // Кръгли чаршафи с ластик
  7: { bg: 'Чаршафи', en: 'Sheets' }, // Чаршафи-KIDS
  8: { bg: 'Калъфки за възглавници', en: 'Pillowcases' }, // Калъфки за възглавници-KIDS
  9: { bg: 'Чаршафи за матрак', en: 'Fitted sheets for a mattress' }, // Чаршафи с ластик-KIDS
  10: { bg: 'Пликове за завивка', en: 'Duvet covers' } // Пликове за завивка-KIDS
};

// GOTOBED ALL PRODUCTS COMPONENTS
const Products = () => {
  const {
    productList,
    loadProductList,
    setCategoryIdUrlParam,
    setSubCategoryIdUrlParam,
    setMaterialIdUrlParam,
    clearProductList,
    pageUrlParam,
    setPageUrlParam,
    materialCategories,
    setMaterialCategories,
    productFilterKey,
    setProductFilterKey
  } = useContext(GlobalContext);
  const { lang, menu } = useContext(GlobalContext);
  const params = useParams(); // for some reason params know that /1/2 is mapped to categoryId and subCatId. How? Why?
  const search = useLocation().search;
  const [showLoadButton, setShowLoadButton] = useState(true);
  // `initialLoading` swaps the grid for skeletons on a fresh page / filter change; `loadingMore`
  // only disables the pagination button, so the products already on screen stay put.
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [currentSubCategory, setCurrentSubCategory] = useState(null);
  const [currentMaterialCategory, setCurrentMaterialCategory] = useState(null);

  const page = new URLSearchParams(search).get('page');
  const size = new URLSearchParams(search).get('size');
  const default_min_size = 12; //set in the backedn unless changed form the frontend to something else
  params['size'] = size;
  params['page'] = page;

  // Dimension pre-filters read from the query string (the size dimension uses `sizeValue` because
  // `size` is already the pagination page-size param). Attached to `params` so they ride along to
  // /products exactly like page/size; axios omits null values, so absent dims send nothing.
  const width = new URLSearchParams(search).get('width');
  const height = new URLSearchParams(search).get('height');
  const length = new URLSearchParams(search).get('length');
  const sizeValue = new URLSearchParams(search).get('sizeValue');
  params['width'] = width;
  params['height'] = height;
  params['length'] = length;
  params['sizeValue'] = sizeValue;

  // Full identity of the current filter; drives the single refetch decision below.
  const filterKey = [
    params.categoryId,
    params.subCategoryId,
    params.materialId || '',
    width || '',
    height || '',
    length || '',
    sizeValue || ''
  ].join('|');

  // Dimension-only query string carried onto each product link so the detail page can preselect.
  const dimsSearchParams = new URLSearchParams();
  if (width) dimsSearchParams.set('width', width);
  if (height) dimsSearchParams.set('height', height);
  if (length) dimsSearchParams.set('length', length);
  if (sizeValue) dimsSearchParams.set('sizeValue', sizeValue);
  const dimsQuery = dimsSearchParams.toString() ? `?${dimsSearchParams.toString()}` : '';

  // Dimension-filter heading, e.g. "Чаршафи за матрак 180 × 200 × 16-20 см" - the product named,
  // then the numbers as width × length (× height when present). The bare numbers alone don't say
  // what they measure; on a fitted sheet they are the mattress, not the sheet.
  const langStrings = strings[lang] || {};
  const cmUnit = langStrings.cmUnit || 'см';
  const dimParts = [width, length, height].filter(Boolean); // width first, then length, then height
  const filterHeadingParts = [];
  if (dimParts.length) filterHeadingParts.push(`${dimParts.join(' × ')} ${cmUnit}`);
  if (sizeValue) filterHeadingParts.push(`${langStrings.withSize || 'с размер'} ${sizeValue}`);
  const headingProduct = FILTER_HEADING_PRODUCT_BY_SUBCATEGORY[Number(params.subCategoryId)];
  const headingProductName =
    headingProduct && (languages.bg === lang ? headingProduct.bg : headingProduct.en);
  const filterHeading = filterHeadingParts.length
    ? [headingProductName, filterHeadingParts.join(', ')].filter(Boolean).join(' ')
    : null;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Map materialAttributeId -> name for the per-card material tag.
  // Reuses the same materialCategories already loaded for the filter bar (no extra fetch), which
  // is fetched for any category/subcategory - so the names resolve on the material-filtered view
  // too, not just the mixed one.
  const materialNameById = React.useMemo(() => {
    const map = {};
    (materialCategories || []).forEach((m) => {
      map[m.id] = m.name_bg;
    });
    return map;
  }, [materialCategories]);

  // WHRE WE SET THE CATEGORY AND SUBCATEGORY ATTRIBUTES HERE???
  const fetchProductListData = async (currentUrlParam) => {
    // params PAGE starts from 0 !!!!!
    params['productId'] = null;
    params['page'] = currentUrlParam;
    try {
      const { data: response } = await axios.get(`/products/?lang=bg`, {
        params
      });
      let newpage = parseInt(currentUrlParam) + 1;
      setPageUrlParam(newpage);

      response.forEach((product) => {
        product['images'].sort(function (a, b) {
          return a['Product_Image'].position - b['Product_Image'].position;
        });
      });

      return response;
    } catch (error) {
      if (error.response) {
        // Not in the 200 response range
        let errorMessage = {
          error:
            '--------------------------------------------------------------------------' +
            new Date() +
            '-  Error while trying to fetchProductListData!!!',
          error_log: error
        };
        fs.appendFile(
          'error_logs.json',
          JSON.stringify(errorMessage) +
            '--------------------------------------------------------------------------------------------------------------'
        );
      }
    }
  };

  const fetchData = async (currentUrlParam, isInitial = false) => {
    if (isInitial) setInitialLoading(true);
    else setLoadingMore(true);

    try {
      const data = await fetchProductListData(currentUrlParam);
      // `fetchProductListData` returns undefined when the request throws, so guard on the array.
      if (data && data != 'error') {
        if (data.length < default_min_size) {
          setShowLoadButton(false);
        }
        await loadProductList(data);
      }
    } finally {
      if (isInitial) setInitialLoading(false);
      else setLoadingMore(false);
    }
  };

  const clearData = async () => {
    await clearProductList();
  };

  // Load the material chips (with per-subcategory counts) that feed the sticky filter bar.
  useEffect(() => {
    const loadMaterialCategories = async () => {
      const { data } = await axios.get(`/materialCategories?lang=bg`, {
        params: { categoryId: params.categoryId, subCategoryId: params.subCategoryId }
      });
      setMaterialCategories(data);
    };
    if (params.categoryId && params.subCategoryId) {
      loadMaterialCategories();
    }
  }, [params.categoryId, params.subCategoryId]);

  // Only the browsed category/subcategory should reset the scroll position. Narrowing by material
  // chip (or a dimension filter) refetches in place, so jumping to the top would yank the page away
  // from the sticky chip bar the user just clicked.
  const scopeKey = `${params.categoryId}|${params.subCategoryId}`;
  const prevScopeKey = useRef(null);

  // Single refetch decision for the whole filter (category/subcategory/material + dimensions).
  // productFilterKey is persisted alongside productList, so a reload/back with a matching filter
  // reuses the cache, while any filter change (including the query-string dimensions) refetches.
  useEffect(() => {
    if (prevScopeKey.current !== scopeKey) {
      window.scrollTo(0, 0);
      prevScopeKey.current = scopeKey;
    }
    if (productFilterKey !== filterKey) {
      clearData();
      setShowLoadButton(true);
      fetchData(0, true);
      setProductFilterKey(filterKey);
    } else if (productList.length === 0) {
      fetchData(0, true);
    } else {
      // Cached list for this exact filter - nothing to fetch, so don't sit on skeletons.
      setInitialLoading(false);
    }
  }, [filterKey]);

  // Keep category/subcategory/material in context (ProductsCategories reads these to decide refetches).
  useEffect(() => {
    const materialId = params.materialId != null ? params.materialId : null;
    if (typeof params.categoryId != 'undefined' && typeof params.subCategoryId != 'undefined') {
      setCategoryIdUrlParam(params.categoryId);
      setSubCategoryIdUrlParam(params.subCategoryId);
      setMaterialIdUrlParam(materialId);
    }
  }, [params]);

  const getMoreProducts = () => {
    fetchData(pageUrlParam);
    createGAevent('Products', 'LOAD_MORE_BUTTON_BUTTON', 'subCategoryId-' + params.subCategoryId);
    createGAevent(
      'Products',
      `LOAD_MORE_BUTTON-subCat-${params.subCategoryId}`,
      'subCategoryId-' + params.subCategoryId
    );
  };

  const persistScrollPosition = (id) => {
    sessionStorage.setItem('scroll-position-product-id-marker', id);
  };

  useEffect(() => {
    if (languages.bg === lang && menu != null) {
      let category = menu.find((categ) => {
        return categ.id == parseInt(params.categoryId);
      });
      let subCategory = category?.subCategories.find((subCat) => {
        return subCat.id == parseInt(params.subCategoryId);
      });
      setCurrentCategory(category);
      setCurrentSubCategory(subCategory);

      // Mixed view has no selected material - only look one up when :materialId is present.
      let material =
        materialCategories && params.materialId
          ? materialCategories.find((mat) => mat.id == params.materialId)
          : null;
      setCurrentMaterialCategory(material ? materialDisplayName(material.name_bg) : null);
    }
  }, [params, menu, materialCategories]);

  return (
    <>
      {currentSubCategory != null && (
        <Helmet>
          <title>
            {getProductSeoMeta(params.categoryId, params.subCategoryId, params.materialId)?.title ||
              `Колекция продукти ${currentSubCategory.name_bg}${
                currentMaterialCategory ? ' ' + currentMaterialCategory : ''
              }`}
          </title>
          <meta
            name="description"
            content={
              getProductSeoMeta(params.categoryId, params.subCategoryId, params.materialId)
                ?.description ||
              'спално бельо, чаршаф, плик, калъфка, чаршаф с ластик, кръгъл чаршаф с ластик, бебешко, детско спално бельо, размери по поръчка, памучен сатен, ранфорс, памук'
            }
          />
          <link rel="canonical" href={window.location.href} />
        </Helmet>
      )}

      <Container
        maxWidth="xl"
        sx={{
          backgroundColor: '',
          paddingTop: 0,
          justifyContent: 'center',
          margin: 0,
          paddingBottom: '40px',
          minHeight: 400
        }}>
        {/* Page intro: breadcrumb → title → intro copy, full width above the grid.
            The title/intro text is the existing per-subcategory SEO copy (ProductSeoHeader); the sx
            below only re-scales its heading to a display serif so we don't fork 24 SEO files.
            ProductSeoHeader also renders the real (visually hidden) <h1>, so SEO is unchanged. */}
        <Box sx={{ pt: 3, pb: 1 }}>
          <Breadcrumbs
            separator="/"
            aria-label="breadcrumb"
            sx={{
              mb: 2,
              fontSize: 13,
              color: 'text.secondary',
              '& .MuiBreadcrumbs-separator': { color: 'text.disabled' },
              '& a:hover': { color: 'gold.text' }
            }}>
            <RouterLink to={routerUrls.root} style={{ color: 'inherit', textDecoration: 'none' }}>
              {strings[lang]?.home || 'Начало'}
            </RouterLink>
            {currentCategory && (
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                {currentCategory.name_bg}
              </Typography>
            )}
            {currentSubCategory && (
              <Typography sx={{ fontSize: 13, color: 'text.primary' }}>
                {currentSubCategory.name_bg}
              </Typography>
            )}
            {currentMaterialCategory && (
              <Typography sx={{ fontSize: 13, color: 'text.primary' }}>
                {currentMaterialCategory}
              </Typography>
            )}
          </Breadcrumbs>

          <Box
            sx={{
              '& p.MuiTypography-h6': {
                fontFamily: (t) => t.typography.h2.fontFamily,
                fontWeight: 500,
                fontSize: { xs: '1.2rem', md: '1.6rem' },
                lineHeight: 1.15,
                letterSpacing: '-0.01em',
                color: 'text.primary',
                mb: 1.5
              },
              '& .MuiTypography-body1': { maxWidth: '72ch' }
            }}>
            <ProductSeoHeader
              categoryId={params.categoryId}
              subCategoryId={params.subCategoryId}
              materialId={params.materialId}
            />
          </Box>

          {filterHeading && (
            <Typography variant="h5" sx={{ mb: 1, color: 'gold.text' }}>
              {filterHeading}
            </Typography>
          )}
        </Box>

        {/* Sticky material filter: shown on both the mixed page and single-material pages so
            selecting a chip narrows the grid in place instead of hiding the filter. The active
            material is highlighted via activeMaterialId. */}
        {params.categoryId && params.subCategoryId && (
          <MaterialFilterBar
            materialCategories={materialCategories}
            activeMaterialId={params.materialId}
            categorySlug={params.category}
            subCategorySlug={params.subcategory}
            categoryId={params.categoryId}
            subCategoryId={params.subCategoryId}
          />
        )}
        <Grid container justify="center" spacing={4} style={{ backgroundColor: '' }}>
          <Grid item lg={3} md={4} xs={12}>
            <ProductFilters />
          </Grid>

          <Grid
            item
            style={{
              backgroundColor: '',
              paddingTop: isMobile ? 0 : 30,
              paddingLeft: isMobile ? '3vh' : '5vh',
              paddingRight: isMobile ? '1vh' : '5vh'
            }}
            lg={9}
            md={8}
            xs={12}>
            <Grid container spacing={3} style={{ backgroundColor: '', minHeight: '800' }}>
              {initialLoading
                ? // A full page of placeholders, so the grid keeps its height while fetching.
                  Array.from({ length: default_min_size }).map((_, index) => (
                    <Grid
                      item
                      key={`skeleton-${index}`}
                      lg={3}
                      md={3}
                      xs={6}
                      sx={{ padding: { xs: 0 } }}>
                      <ProductCardSkeleton />
                    </Grid>
                  ))
                : productList &&
                  productList.map((ProductItem) => (
                    <Grid
                      item
                      key={ProductItem.id}
                      lg={3}
                      md={3}
                      xs={6}
                      sx={{ padding: { xs: 0 } }}>
                      <Product
                        Product={ProductItem}
                        onSelect={persistScrollPosition}
                        attributeQuery={dimsQuery}
                        materialName={materialNameById[ProductItem.materialAttributeId]}
                      />
                    </Grid>
                  ))}
            </Grid>

            <Box textAlign="center" sx={{ p: 1, mt: 5 }}>
              {!initialLoading && showLoadButton && (
                <Button
                  variant="contained"
                  onClick={getMoreProducts}
                  disabled={loadingMore}
                  // Filled accent, white label - a deliberate exception to the palette note in
                  // theme.js, which reserves gold.fill for ink type because white on it is only
                  // 2.77:1, below the 4.5:1 AA needs. Overrides containedPrimary's ink label;
                  // hover keeps the theme's gold.deep so the state change still reads.
                  sx={{
                    backgroundColor: 'gold.fill',
                    color: '#FFFFFF',
                    '&:hover': { backgroundColor: 'gold.deep' },
                    // sx outranks the theme's disabled styling, so restate it: the button stays
                    // disabled for the whole fetch and shouldn't keep reading as clickable.
                    '&.Mui-disabled': {
                      backgroundColor: 'action.disabledBackground',
                      color: 'text.disabled'
                    }
                  }}
                  startIcon={
                    loadingMore ? <CircularProgress size={16} color="inherit" /> : undefined
                  }>
                  {strings[lang].loadMoreButton}
                </Button>
              )}
              {!initialLoading && !showLoadButton && (
                <Typography> {strings[lang].noMoreProducts}</Typography>
              )}
            </Box>
            <ProductSeoBody
              categoryId={params.categoryId}
              subCategoryId={params.subCategoryId}
              materialId={params.materialId}
            />
          </Grid>
        </Grid>
      </Container>
    </>
  );
};
export default Products;
